import type { IDailyStat, IFilterState } from './types';

/** Earliest date selectable in the dashboard (30 Jul 2024). */
export const MOCKUP_LOGS_MIN_DATE = '2024-07-30';

export function clampDateToRange(iso: string, min: string, max: string): string {
  if (iso < min) return min;
  if (iso > max) return max;
  return iso;
}

export function clampFilterRange(start: string, end: string, min: string, max: string): { start: string; end: string } {
  let s = clampDateToRange(start, min, max);
  let e = clampDateToRange(end, min, max);
  if (s > e) s = e;
  return { start: s, end: e };
}

/** Default selection: last 7 days ending on maxDate. */
export function default7dFilter(maxDate: string): Pick<IFilterState, 'start' | 'end' | 'preset'> {
  const end = maxDate;
  let start = dateAdd(maxDate, -6);
  if (start < MOCKUP_LOGS_MIN_DATE) start = MOCKUP_LOGS_MIN_DATE;
  return { start, end, preset: '7d' };
}

export function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US');
}

export function fmtPct(n: number | null | undefined, d = 2): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(d) + '%';
}

export function fmtSec(n: number | null | undefined, d = 2): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(d) + 's';
}

export function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return (part / whole) * 100;
}

export function delta(curr: number, prev: number | null | undefined): number | null {
  if (prev === undefined || prev === null || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
}

export function dateAdd(isoDate: string, days: number): string {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function dateDiff(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

export function prevPeriod(start: string, end: string, minDate = MOCKUP_LOGS_MIN_DATE): { cstart: string; cend: string } {
  const len = dateDiff(start, end) + 1;
  let cend = dateAdd(start, -1);
  let cstart = dateAdd(cend, -(len - 1));
  if (cstart < minDate) cstart = minDate;
  if (cend < minDate) cend = minDate;
  if (cstart > cend) cstart = cend;
  return { cstart, cend };
}

export function shortDate(d: string): string {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function alignComparison(currDays: IDailyStat[], prevDays: IDailyStat[] | null): IDailyStat[] | null {
  if (!prevDays) return null;
  const n = currDays.length;
  return prevDays.slice(-n);
}

export function readFilterFromSearch(sp: URLSearchParams): Partial<IFilterState> {
  return {
    start: sp.get('start') || '',
    end: sp.get('end') || '',
    compare: sp.get('compare') === '1',
    cstart: sp.get('cstart') || '',
    cend: sp.get('cend') || '',
    preset: sp.get('preset') || '',
  };
}

export function writeFilterToSearch(s: IFilterState): string {
  const sp = new URLSearchParams();
  sp.set('start', s.start);
  sp.set('end', s.end);
  if (s.compare) sp.set('compare', '1');
  if (s.cstart) sp.set('cstart', s.cstart);
  if (s.cend) sp.set('cend', s.cend);
  if (s.preset && s.preset !== 'custom') sp.set('preset', s.preset);
  return sp.toString();
}

export interface IDeltaBadgeOpts {
  inverted?: boolean;
}

export function deltaBadgeClass(curr: number, prev: number | null | undefined, opts: IDeltaBadgeOpts = {}): 'up' | 'down' | 'flat' {
  const d = delta(curr, prev);
  if (d === null || Number.isNaN(d)) return 'flat';
  const inverted = opts.inverted;
  const dir = d > 0.05 ? 'up' : d < -0.05 ? 'down' : 'flat';
  if (dir === 'up') return inverted ? 'down' : 'up';
  if (dir === 'down') return inverted ? 'up' : 'down';
  return 'flat';
}

export function deltaBadgeArrow(curr: number, prev: number | null | undefined, opts: IDeltaBadgeOpts = {}): string {
  const d = delta(curr, prev);
  if (d === null || Number.isNaN(d)) return '';
  const dir = d > 0.05 ? 'up' : d < -0.05 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–';
  const cls = deltaBadgeClass(curr, prev, opts);
  const pctAbs = Math.abs(d).toFixed(1);
  return `<span class="delta ${cls}" title="vs. previous period">${arrow} ${pctAbs}%</span>`;
}

export function deltaInlineHtml(curr: number, prev: number | null | undefined, opts: IDeltaBadgeOpts = {}): string {
  const d = delta(curr, prev);
  if (d === null || Number.isNaN(d)) return '';
  const cls = deltaBadgeClass(curr, prev, opts);
  const arrow = d > 0.05 ? '▲' : d < -0.05 ? '▼' : '–';
  return `<span class="delta-inline ${cls}">${arrow}${Math.abs(d).toFixed(1)}%</span>`;
}
